/**
 * A DAY AND A TIME, AS THE ARTBOARDS WRITE THEM: "Tuesday 27 October", "Mon 26 Oct" on a day chip,
 * "2 October" and "12 Oct" on Payments, and "09:00". And the days a move offers, which are the
 * business's days too.
 *
 * The places give a day as `YYYY-MM-DD`, already the business's own day (the Worker's
 * `lib/localday.ts`), and a time as `HH:MM`, already local. So a day's words come from the calendar
 * alone, never from the phone's clock or its time zone, which would show a Florida Tuesday as a
 * Wednesday to a client reading it from abroad.
 *
 * Anything that is not a real day or a real time gives null, and a screen draws nothing for it
 * rather than a wrong day.
 */

/** The weekdays as JavaScript numbers them, Sunday first. The month view's heads are made from them. */
export const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/** The months, January first, as every day and the month view's heading write them. */
export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/**
 * A `YYYY-MM-DD` day in words, "Tuesday 27 October", or null when it is not a real day. The date
 * and its month are joined by a no-break space, as the Payments artboard holds "2&nbsp;October"
 * together (`Payments.dc.html` l.56), so a line breaks before the date and never between it and
 * its month: "Visit of Wednesday / 30 September", never "Visit of Wednesday 30 / September".
 */
export function dayInWords(day: string): string | null {
  const calendar = calendarOf(day);
  if (calendar === null) return null;
  return `${WEEKDAYS[calendar.getUTCDay()]} ${calendar.getUTCDate()}\u00a0${MONTHS[calendar.getUTCMonth()]}`;
}

/** A local `HH:MM` time on the 24-hour clock the artboards draw, or null when it is not one. */
export function timeOfDay(time: string): string | null {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(time) ? time : null;
}

/** The week as the business writes its days, Monday first, as JavaScript numbers each day. */
const WEEK_FROM_MONDAY = [1, 2, 3, 4, 5, 6, 0] as const;

/**
 * Weekdays, as JavaScript numbers them, 0 being Sunday, in the words of ops's `businessHoursWords`
 * (ops's `docs/seams/home.md` at 0b51e49, section 5, "The business day"): "every day" for all seven;
 * the first and the last day of the run joined by "to" when three to six days make one unbroken run,
 * over the end of the week too, so "Sunday to Friday"; otherwise each day by name, from Monday,
 * joined as a sentence joins them, "Monday, Wednesday and Friday". The order given and a day given
 * twice change nothing. Null for no days, or for any that is not a weekday, rather than days that
 * are wrong.
 */
export function weekdaysInWords(days: readonly number[]): string | null {
  if (days.length === 0 || !days.every((day) => Number.isInteger(day) && day >= 0 && day <= 6)) {
    return null;
  }
  const given = new Set(days);
  if (given.size === 7) return 'every day';
  if (given.size >= 3) {
    // The run starts at the one day whose day before is not given, and is unbroken when it reaches
    // every day given before it meets one that is not.
    const first = [...given].find((day) => !given.has((day + 6) % 7))!;
    let length = 0;
    while (given.has((first + length) % 7)) length += 1;
    if (length === given.size) {
      return `${WEEKDAYS[first]!} to ${WEEKDAYS[(first + length - 1) % 7]!}`;
    }
  }
  const named = WEEK_FROM_MONDAY.filter((day) => given.has(day)).map((day) => WEEKDAYS[day]!);
  const last = named.pop()!;
  return named.length === 0 ? last : `${named.join(', ')} and ${last}`;
}

/**
 * A local `HH:MM` time on the twelve-hour clock ops's `businessHoursWords` writes, "9:00 am" and
 * "6:00 pm", noon "12:00 pm" and midnight "12:00 am", or null when it is not a time.
 */
export function twelveHourTime(time: string): string | null {
  if (timeOfDay(time) === null) return null;
  const [hours, minutes] = time.split(':').map(Number) as [number, number];
  const hour = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour}:${String(minutes).padStart(2, '0')} ${hours < 12 ? 'am' : 'pm'}`;
}

/**
 * The business's hours as ops pushes them on an invoice its saved method pays (`autopay.
 * business_hours`), in the words of ops's `businessHoursWords`, `<days>, <start> to <end>`:
 * "Monday to Saturday, 9:00 am to 6:00 pm". `start` and `end` are `HH:MM` in Florida, the start
 * included and the end not, and `days` the weekdays as JavaScript numbers them. Her page says it
 * after "during business hours: " and ends it with a full stop. Null when any part is not real,
 * rather than hours that are wrong.
 */
export function businessHoursWords(hours: {
  readonly start: string;
  readonly end: string;
  readonly days: readonly number[];
}): string | null {
  const days = weekdaysInWords(hours.days);
  const start = twelveHourTime(hours.start);
  const end = twelveHourTime(hours.end);
  return days === null || start === null || end === null ? null : `${days}, ${start} to ${end}`;
}

/**
 * A `YYYY-MM-DD` day as a calendar date, or null when it is not a real day. The arithmetic is in
 * UTC, which has no clock changes, so the day is the day it names.
 */
function calendarOf(day: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return null;
  const [year, month, date] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const calendar = new Date(Date.UTC(year, month - 1, date));
  return calendar.getUTCFullYear() === year &&
    calendar.getUTCMonth() === month - 1 &&
    calendar.getUTCDate() === date
    ? calendar
    : null;
}

/** A calendar date back as `YYYY-MM-DD`. */
const dayOf = (calendar: Date) => calendar.toISOString().slice(0, 10);

/**
 * How many days `today` is after `day`, both `YYYY-MM-DD` business days from the place: 0 on the day
 * itself, 1 the day after, and less than 0 for a day still to come. Counted on the calendar, in UTC,
 * so a change of the clocks in Florida never makes a day of 23 or 25 hours count short or long. Null
 * when either is not a real day.
 */
export function daysSince(day: string, today: string): number | null {
  const from = calendarOf(day);
  const to = calendarOf(today);
  if (from === null || to === null) return null;
  return Math.round((to.getTime() - from.getTime()) / DAY_MS);
}

/**
 * The business's time zone, Florida's, as the Worker's `lib/localday.ts` holds it (provisional P23),
 * and never the phone's own.
 */
const BUSINESS_TIME_ZONE = 'America/New_York';

/** The day an instant falls on in the business's zone, written `YYYY-MM-DD` (`en-CA`'s own order). */
const FLORIDA_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** An instant as the Worker writes one, ISO 8601 with its zone. */
const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

/**
 * The business's day an instant falls on, `YYYY-MM-DD` in Florida, whatever the phone's own zone, or
 * null when it is not an instant: the day a visit's photos may be seen until, from when the first of
 * their links runs out (home #21), which the Worker gives as an instant rather than a day.
 */
export function dayOfInstant(instant: string): string | null {
  if (!INSTANT.test(instant)) return null;
  const at = Date.parse(instant);
  if (Number.isNaN(at)) return null;
  const day = FLORIDA_DAY.format(new Date(at));
  return calendarOf(day) === null ? null : day;
}

/**
 * A `YYYY-MM-DD` day as the Schedule sheet's day chips write it, "Mon 26 Oct", or null when it is
 * not a real day.
 */
export function shortDay(day: string): string | null {
  const calendar = calendarOf(day);
  if (calendar === null) return null;
  const weekday = WEEKDAYS[calendar.getUTCDay()]!.slice(0, 3);
  const month = MONTHS[calendar.getUTCMonth()]!.slice(0, 3);
  return `${weekday} ${calendar.getUTCDate()} ${month}`;
}

/**
 * A `YYYY-MM-DD` day as the Payments artboard writes when a payment came, "2 October", the date and
 * the month with no weekday and no year, or null when it is not a real day. The two are joined by a
 * no-break space, as the artboard holds them together (`Payments.dc.html` l.56, "2&nbsp;October").
 */
export function dateInWords(day: string): string | null {
  const calendar = calendarOf(day);
  if (calendar === null) return null;
  return `${calendar.getUTCDate()}\u00a0${MONTHS[calendar.getUTCMonth()]}`;
}

/**
 * A `YYYY-MM-DD` day with its year, "6 October 2026", or null when it is not a real day: the days an
 * agreement holds on Your agreement (ops's section 12.1), which can be in another year than this
 * one, so the year is always said. The date and its month are held together as `dateInWords` holds
 * them, and the line may break before the year. Never written straight after "since": "since" and a
 * year together are a claim of how long the business has been going, which `what-we-cannot-say.md`
 * forbids, and this puts the date and the month between them wherever it is used.
 */
export function fullDate(day: string): string | null {
  const calendar = calendarOf(day);
  if (calendar === null) return null;
  return `${dateInWords(day)!} ${calendar.getUTCFullYear()}`;
}

/**
 * A `YYYY-MM-DD` day as ops's `chargedWords` writes the day a charge fell on in Florida (ops's
 * `docs/seams/home.md` at 0b51e49, section 5), "Tuesday, October 27": the weekday, a comma, the
 * month and the date, and no year. Null when it is not a real day.
 */
export function weekdayMonthDate(day: string): string | null {
  const calendar = calendarOf(day);
  if (calendar === null) return null;
  return `${WEEKDAYS[calendar.getUTCDay()]}, ${MONTHS[calendar.getUTCMonth()]} ${calendar.getUTCDate()}`;
}

/**
 * A `YYYY-MM-DD` day as the seam writes when a saved method was agreed, "12 Oct", the date and the
 * short month, or null when it is not a real day. Never the year: "since" and a year together are a
 * claim of how long the business has been going, which `what-we-cannot-say.md` forbids, and the
 * claims guard refuses the pair wherever it is written.
 */
export function shortDate(day: string): string | null {
  const calendar = calendarOf(day);
  if (calendar === null) return null;
  return `${calendar.getUTCDate()} ${MONTHS[calendar.getUTCMonth()]!.slice(0, 3)}`;
}

/**
 * How far ahead a day of an ask about her regular cleans may be: day 365 included, today being day
 * 0, as ops takes each (its section 12.4, "every day today or later in Florida and within 365 days",
 * read as `visit.move` reads its ninety, provisional P64). The request door holds the same bound
 * (`api/src/seam/requests.ts`, `CLEANS_DAYS_AHEAD`), which a test keeps equal to this.
 */
export const CLEANS_DAYS_AHEAD = 365;

/**
 * The `YYYY-MM-DD` day `count` days after `day`, counted on the calendar, or null when `day` is not
 * a real day: the last day an ask about her regular cleans may name, `CLEANS_DAYS_AHEAD` after the
 * place's today.
 */
export function daysAfter(day: string, count: number): string | null {
  const calendar = calendarOf(day);
  return calendar === null ? null : dayOf(new Date(calendar.getTime() + count * DAY_MS));
}

/** How many days a move is offered, as the artboard draws them. */
const DAYS_OFFERED = 4;

/**
 * How far ahead a move may name a day: up to day 90 included, today being day 0. Ops takes each day
 * "today or later and within ninety days, day 90 included", today in Florida being day 0, so on
 * 2026-10-15 the last day taken is 2027-01-13 (its section 4 at 0b51e49, `visit.move`, home's Q6),
 * and refuses any other as final. The request door holds the same bound
 * (`api/src/seam/requests.ts`, `days_that_suit`), which a test keeps equal to this.
 */
export const MOVE_DAYS_AHEAD = 90;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * THE DAYS A MOVE OFFERS, PENDING THE OWNER: the four days nearest the visit, Monday to Saturday,
 * after today and up to day 90 included, never the visit's own day, in the calendar's order. Two
 * days as near as each other, one before the visit and one after, go to the later one, which is how
 * the artboard's chips come out for a Tuesday: Monday, Wednesday, Thursday and Friday of its week.
 * Both days are the business's (`today` is the place's own), so the phone's clock is never read. A
 * visit near day 90 is offered fewer days after it, and one far past it none, so no move is offered.
 *
 * Nothing for a day that is not a real one. If the owner chooses otherwise, this function changes
 * and nothing else.
 */
export function daysThatMightSuit(visitDay: string, today: string): string[] {
  const visit = calendarOf(visitDay);
  const now = calendarOf(today);
  if (visit === null || now === null) return [];
  /** Day 90, the last day ops takes. */
  const end = new Date(now.getTime() + MOVE_DAYS_AHEAD * DAY_MS);
  const near: { day: string; distance: number; after: boolean }[] = [];
  // Two weeks either side holds four days after today for any visit from today on, unless day 90
  // cuts them short.
  for (let offset = -14; offset <= 14; offset += 1) {
    const calendar = new Date(visit.getTime() + offset * DAY_MS);
    const sunday = calendar.getUTCDay() === 0;
    if (offset === 0 || sunday || calendar <= now || calendar > end) continue;
    near.push({ day: dayOf(calendar), distance: Math.abs(offset), after: offset > 0 });
  }
  return near
    .sort((a, b) => a.distance - b.distance || Number(b.after) - Number(a.after))
    .slice(0, DAYS_OFFERED)
    .map(({ day }) => day)
    .sort();
}
